import {
  type Aabb2,
  type Point2,
  type ReadableStore,
  type Store,
  type ViewportSize,
  type ViewportState,
  aabbCenter,
  fitAabb,
  panByScreenDelta,
  rotateViewport,
  screenToWorld,
  worldToScreen,
  zoomAtScreenPoint,
} from '@frontend/viewer-core';

import { visibleBounds } from './deriveModel';
import { lerpViewport } from './entityFrame';
import type { DrawingModel, ViewportManager } from './types';

type Dependencies = {
  viewportStore: Store<ViewportState>;
  /** Read-only: the surface binding is the one thing that measures and writes this. */
  sizeStore: ReadableStore<ViewportSize>;
  getModel: () => DrawingModel | null;
  getHiddenLayers: () => ReadonlySet<number>;
};

/**
 * `ViewportManager` plus the one capability that's only needed by another
 * controller in this folder (`layerController`'s "frame this layer"), not by
 * the outside world — so it stays off the public `ViewportManager` type.
 */
export type ViewportController = ViewportManager & {
  /** Frames `bounds` exactly, keeping the current rotation. `null` still resets the view. */
  frameBounds(bounds: Aabb2 | null): void;
  /**
   * Linearly moves center and scale to `target` over `durationMs`.
   *
   * A later viewport change — another animation, a pan, a zoom — cancels the
   * one in flight, so a gesture takes over from wherever the lerp had reached.
   */
  animateTo(target: ViewportState, durationMs: number): void;
  /** Stops an in-flight `animateTo` without changing the current view. */
  cancelAnimation(): void;
};

/**
 * Owns the viewport and everything that reads or moves it.
 */
export const createViewportController = ({
  viewportStore,
  sizeStore,
  getModel,
  getHiddenLayers,
}: Dependencies): ViewportController => {
  /** Scale a fit would produce right now — the reference the zoom clamp is relative to. */
  const currentFittedScale = (): number =>
    fitAabb(
      visibleBounds(getModel(), getHiddenLayers()),
      sizeStore.getState(),
      {
        quarterTurns: viewportStore.getState().quarterTurns,
      },
    ).scale;

  let animation = 0;
  let frame: number | null = null;

  const cancelAnimation = () => {
    animation += 1;
    if (frame !== null) {
      cancelAnimationFrame(frame);
      frame = null;
    }
  };

  const isSameView = (a: ViewportState, b: ViewportState): boolean =>
    a.center[0] === b.center[0] &&
    a.center[1] === b.center[1] &&
    a.scale === b.scale &&
    a.quarterTurns === b.quarterTurns;

  const animateTo = (target: ViewportState, durationMs: number) => {
    cancelAnimation();
    const id = animation;
    const start = viewportStore.getState();
    if (durationMs <= 0 || isSameView(start, target)) {
      viewportStore.setState(() => target);
      return;
    }

    const startedAt = performance.now();
    const step = () => {
      if (id !== animation) {
        return;
      }
      frame = null;
      const t = Math.min(1, (performance.now() - startedAt) / durationMs);
      const next = lerpViewport(start, target, t);
      viewportStore.setState((previous) =>
        isSameView(previous, next) ? previous : next,
      );
      if (id !== animation || t >= 1) {
        return;
      }
      frame = requestAnimationFrame(step);
    };

    frame = requestAnimationFrame(() => step());
  };

  const frameBounds = (bounds: Aabb2 | null) => {
    cancelAnimation();
    viewportStore.setState((previous) =>
      fitAabb(bounds, sizeStore.getState(), {
        quarterTurns: previous.quarterTurns,
      }),
    );
  };

  const fit = ({ visibleOnly = true }: { visibleOnly?: boolean } = {}) => {
    const model = getModel();
    const bounds = visibleOnly
      ? visibleBounds(model, getHiddenLayers())
      : model?.bounds ?? null;
    frameBounds(bounds);
  };

  return {
    getState: viewportStore.getState,
    subscribe: viewportStore.subscribe,
    size: { getState: sizeStore.getState, subscribe: sizeStore.subscribe },

    set(next) {
      cancelAnimation();
      viewportStore.setState(() => next);
    },

    panBy(deltaX, deltaY) {
      cancelAnimation();
      viewportStore.setState((previous) =>
        panByScreenDelta(previous, deltaX, deltaY),
      );
    },

    zoomAt(screenPoint, factor) {
      cancelAnimation();
      const size = sizeStore.getState();
      const fittedScale = currentFittedScale();
      viewportStore.setState((previous) =>
        zoomAtScreenPoint(previous, size, screenPoint, factor, fittedScale),
      );
    },

    rotate(delta) {
      cancelAnimation();
      const pivot = aabbCenter(getModel()?.bounds);
      viewportStore.setState((previous) =>
        rotateViewport(previous, delta, pivot),
      );
    },

    fit,
    frameBounds,
    animateTo,
    cancelAnimation,

    reset() {
      viewportStore.setState((previous) =>
        previous.quarterTurns === 0
          ? previous
          : { ...previous, quarterTurns: 0 },
      );
      fit({ visibleOnly: true });
    },

    worldToScreen(world: Point2) {
      return worldToScreen(
        world,
        viewportStore.getState(),
        sizeStore.getState(),
      );
    },

    screenToWorld(screen: Point2) {
      return screenToWorld(
        screen,
        viewportStore.getState(),
        sizeStore.getState(),
      );
    },
  };
};
